import { Component, OnDestroy, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { of, Subscription, switchMap } from 'rxjs';
import moment from 'moment';

import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { User } from '../../shared/models/user';
import { UserService } from '../../shared/services/user.service';
import {
	EfeasibilityInfo,
	ProductCategoryDisplay, ProductCategoryResponse, SubCategory
} from '../models/efeasibility';
import { WorkerService } from '../../shared/services/worker.service';
import { TranslationService } from '../../shared/services/translation.service';
import { OrderBy, OrderByDirection } from '../../shared/utils/constant';
import { EfeasibilityService } from '../services/efeasibility.service';
import { BocClient } from '../../shared/models/boc-client-transac';

import { EfeasibilityFirstScreenComponent }
	from './wizard/efeasibility-first-screen/efeasibility-first-screen.component';
import { DialogFileUploadEfeasibilityComponent }
	from './wizard/efeasibility-first-screen/dialog-file-upload-efeasibility/dialog-file-upload-efeasiblity.component';

const LIMIT = 1500;
const PAGE = 1;
const SORT_WAY = OrderByDirection.ASC;
const SORT_BY = 'name';

@Component({
	selector: 'copo-efeasibility',
	styleUrl: './efeasibility.component.css',
	templateUrl: './efeasibility.component.html',
	imports: [
		BackButtonComponent,
		TranslateModule,
		EfeasibilityFirstScreenComponent,
		MatIconModule,
		MatButtonModule,
		CommonModule,
		MatDialogModule
	]
})
export class EFeasibilityComponent implements OnDestroy {
	@ViewChild(EfeasibilityFirstScreenComponent) efeasibilityFirstScreen?: EfeasibilityFirstScreenComponent;
	user: User;
	efeasibilityItems: EfeasibilityInfo[] = [];
	displayGrid = false;
	selectedSoldToName?: string;
	selectedProductCategory?: string;
	selectedProductSubCategory?: string;
	productCategories: ProductCategoryDisplay[] = [];
	steelgradesList: Record<string, string[]> = {};
	standardSteelgrades: string[] = [];
	soldTos?: BocClient[];
	productSubCategories: SubCategory[] = [];
	isProductCategory = false;
	isProductSubCategory = false;
	isStandardSteelgrade = false;
	sub?: Subscription;
	soldToSub?: Subscription;
	audienceFields: string[] = [
		'id',
		'name',
		'members',
		'audienceProductCategories'
	];

	private dialogSubscription?: Subscription;

	constructor(
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		readonly workerService: WorkerService,
		private readonly dialog: MatDialog,
		private readonly efeasibilityService: EfeasibilityService
	) {
		this.user = this.userService.user;
		this.getSoldTos();
		this.getAudiences();
	}

	openFileImportDialog() {
		const dialogRef = this.dialog.open(DialogFileUploadEfeasibilityComponent, {
			data: { productCategoriesList: this.productCategories }
		});
		this.dialogSubscription = dialogRef.afterClosed().subscribe(async (result: EfeasibilityInfo[]) => {
			if (result.length > 0) {
				this.displayGrid = true;
				this.efeasibilityItems.push(...result);
				for (const item of this.efeasibilityItems) {
					item.soldToId = this.soldTos
						? this.soldTos.find(
							soldToDetails => soldToDetails.name === item.soldToName
						)?.businessId ?? '' : '';
				}
				await this.efeasibilityService.addEfeasibility([...result]);
			}
			if (!this.efeasibilityFirstScreen) {
				return;
			}
			if (this.efeasibilityFirstScreen.efeasibilityItems.length > 0) {
				this.efeasibilityFirstScreen.setRowData();
				this.efeasibilityFirstScreen.refreshGrid();
			}
		});
	}

	ngOnDestroy() {
		if (this.dialogSubscription) {
			this.dialogSubscription.unsubscribe();
		}
	}

	handleSoldToNameChange(value: string) {
		this.selectedSoldToName = value;
	}

	handleProductCategoryChange(value: string) {
		this.selectedProductCategory = value;
		for (const item of this.productCategories) {
			if (item.productCategoryCode === this.selectedProductCategory) {
				this.productSubCategories = item.productSubCategories;
				break;
			}
		}
	}

	handleProductSubCategoryChange(value: string) {
		this.selectedProductSubCategory = value;
		this.isProductSubCategory = Boolean(this.selectedProductSubCategory);
	}

	handleSteelgradesChange(data: { steelgrades: Record<string, string[]>; standardSteelgrades: string[] }) {
		this.steelgradesList = data.steelgrades;
		this.standardSteelgrades = data.standardSteelgrades;
		this.isStandardSteelgrade = Boolean(this.standardSteelgrades.length > 0);
	}

	getSoldTos() {
		this.soldToSub = this.userService.getSoldTosTransac('PFEAS')
			.pipe(
				switchMap(businessIds => {
					if (businessIds.length === 0) {
						return of(undefined);
					}

					return this.userService.getBocs(
						['name', 'businessId'],
						businessIds.map(x => `businessId:${ x }`).join(' OR ')
					);
				})
			).subscribe({
				next: soldToList => {
					this.soldTos = soldToList?.results;
				}
			});
	}

	getAudiences() {
		this.sub = this.efeasibilityService.getAudiences(
			this.audienceFields,
			undefined,
			PAGE,
			LIMIT,
			new OrderBy(SORT_BY, SORT_WAY)
		).subscribe({
			next: res => {
				const productCategories: ProductCategoryResponse[] = [];
				for (const prCat of res.results) {
					if (prCat.productCategories) {
						for (const item of prCat.productCategories) {
							productCategories.push(item);
						}
					}
				}

				const groupedCategories: Record<string, ProductCategoryDisplay> = {};
				for (const item of productCategories) {
					const { productCategoryCode, productCategory, productSubCategoryCode, productSubCategory } = item;
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					if (!groupedCategories[productCategoryCode]) {
						groupedCategories[productCategoryCode] = {
							productCategoryCode,
							productCategory,
							productSubCategories: <SubCategory[]>[], // Initialize as an empty array
						};
					}

					// Check if the productSubCategoryCode is already in the productSubCategories array
					const existingSubCategory = groupedCategories[productCategoryCode].productSubCategories
						.find(subCat => subCat.productSubCategoryCode === productSubCategoryCode);

					if (!existingSubCategory) {
						// If the subCategory doesn't exist, create it and push it
						const subCategory = {
							productSubCategoryCode,
							productSubCategory,
						};

						groupedCategories[productCategoryCode].productSubCategories.push(subCategory);
					}
				}
				this.productCategories = Object.keys(groupedCategories).map(key => groupedCategories[key]);
			},
			error: (err: unknown) => {
				console.error('error requesting audiences', err);
			}
		});
	}

	getExcelTemplate() {
		const soldTo = this.soldTos?.find(
			soldToDetails => soldToDetails.businessId === this.selectedSoldToName
		);
		const productCategory = this.productCategories.find(
			cat => cat.productCategoryCode === this.selectedProductCategory
		);
		const productSubCategory = this.productSubCategories.find(
			cat => cat.productSubCategoryCode === this.selectedProductSubCategory
		);
		const soldToName = soldTo ? [soldTo.name] : [];
		const productCategoryName = productCategory ? productCategory.productCategory : '';
		const productSubCategoryName = productSubCategory ? productSubCategory.productSubCategory : '';
		const sheetsData = [
			{
				sheetName: this.translationService.getTR('efeasibility.excel.file.name'),
				formattedData: [
					[
						this.translationService.getTR('guarantee.customer.name'),
						this.translationService.getTR('datacenter-filter.product-category'),
						this.translationService.getTR('datacenter.offer.subcategory'),
						this.translationService.getTR('standard-steelgrade'),
						this.translationService.getTR('dashboard.steelgrade'),
						this.translationService.getTR('shipment.filter.width'),
						this.translationService.getTR('shipment.filter.thickness')
					],
					[]
				],
				columnTypes: ['text', 'text', 'text', 'text', 'text', 'number', 'number']
			},
			{
				sheetName: this.translationService.getTR('guarantee.customer.name'),
				formattedData: [
					[this.translationService.getTR('guarantee.customer.name')],
					soldToName
				],
				columnTypes: ['text']
			},
			{
				sheetName: this.translationService.getTR('datacenter-filter.product-category'),
				formattedData: [
					[
						this.translationService.getTR('datacenter-filter.product-category'),
						this.translationService.getTR('datacenter.offer.subcategory')
					],
					[productCategoryName, productSubCategoryName]
				],
				columnTypes: ['text', 'text']
			},

			{
				sheetName: this.translationService.getTR('dashboard.steelgrade'),
				formattedData: [
					[
						this.translationService.getTR('standard-steelgrade'),
						this.translationService.getTR('dashboard.steelgrade')
					],
					...this.standardSteelgrades.flatMap((standardSteelgrade: string) =>
						this.steelgradesList[standardSteelgrade].map((steelgrade: string) =>
							[standardSteelgrade, steelgrade]))
				],
				columnTypes: ['text']
			}
		];
		this.exportToExcel(sheetsData)
			.then(() => {
				console.info('Export completed');
			})
			.catch((error: unknown) => {
				console.error('Export failed:', error);
			});
	}

	async exportToExcel(sheets: {
		sheetName: string;
		formattedData: string[][];
		columnTypes: (string | string[] | undefined)[];
	}[]) {
		return new Promise<void>((resolve, reject) => {
			const worker = this.workerService.createWorkerExcel();
			const fileName = `Feasibility template-${ moment().format('YYYY MM DD') }.xlsx`;

			worker.addEventListener('message', ({ data }: { data: string }) => {
				const link = document.createElement('a');
				link.download = fileName;
				link.href = data;
				link.append(document.createTextNode(fileName));
				document.body.append(link);
				link.click();
				link.remove();
				resolve();
				// let 10 seconds to the browser for downloading before revoking the url
				setTimeout(() => {
					URL.revokeObjectURL(data);
					worker.terminate();
				}, 10_000);
			});

			worker.addEventListener('error', error => {
				reject(new Error('Error in worker', { cause: error }));
				worker.terminate();
			});

			const sheetData: {
				fileName: string;
				rows: string[][];
				workSheetName: string;
				columnTypes: (string | string[] | undefined)[];
			}[] = [];
			for (const sheet of sheets) {
				const entry = {
					fileName: sheet.sheetName,
					rows: sheet.formattedData,
					workSheetName: sheet.sheetName,
					columnTypes: sheet.columnTypes
				};
				sheetData.push(entry);
			}

			worker.postMessage({
				sheets: sheetData
			});
		});
	}
}
